<template>
	<b-col
		v-if="hasPerformance"
		cols="12"
		class="mb-1"
	>
		<label>
			<strong>
				PERÍODO DE EVALUACIÓN
				<span class="text-info">*</span>
			</strong>
		</label>

		<b-alert
			show
			variant="info"
			class="mb-1"
		>
			<div class="alert-body">
				<feather-icon
					icon="InfoIcon"
					size="15"
					class="mr-50"
				/>
				<span>
					Las evaluaciones se generan los <strong>05 de cada mes</strong> por
					defecto.
				</span>
			</div>
		</b-alert>

		<b-form-group class="mb-1">
			<div class="d-flex align-items-center mb-25">
				<b-form-radio
					v-model="mode"
					name="performance-mode"
					value="FORWARD"
					class="mb-0"
				>
					De aquí en adelante
				</b-form-radio>
				<b-badge
					pill
					variant="light-primary"
					class="ml-1"
				>
					Inicia el {{ forwardStartDate }}
				</b-badge>
			</div>

			<b-form-radio
				v-model="mode"
				name="performance-mode"
				value="BACKFILL"
			>
				Incluir mes actual / meses anteriores
			</b-form-radio>
		</b-form-group>

		<template v-if="mode === 'BACKFILL'">
			<label for="manual_cycles">
				<strong>
					MESES A GENERAR
					<span class="text-danger">*</span>
				</strong>
			</label>
			<validation-provider
				#default="{ errors }"
				rules="required"
				name="meses a generar"
			>
				<v-select
					id="manual_cycles"
					multiple
					placeholder="Seleccione uno o más meses"
					v-model="selected"
					:options="cycleOptions"
					:loading="loading"
					@input="emitValue"
				/>

				<small class="text-danger">{{ errors[0] }}</small>
			</validation-provider>

			<small
				v-if="!loading && !cycleOptions.length"
				class="text-danger"
			>
				No hay meses disponibles para generar.
			</small>
			<small class="d-block text-muted mt-25">
				Se generarán las evaluaciones de los meses seleccionados. Los meses que ya
				tengan evaluación se omiten automáticamente.
			</small>
		</template>
	</b-col>
</template>

<script>
import { ref, watch } from "@vue/composition-api"
import { ValidationProvider } from "vee-validate"
import useNotifications from "@notifications"
import { required } from "@validations"
import vSelect from "vue-select"
import moment from "moment"
import axios from "@axios"

export default {
	components: {
		ValidationProvider,
		required,
		vSelect
	},
	props: {
		hasPerformance: {
			type: [Boolean, Number],
			default: false
		},
		value: {
			type: Array,
			default: () => []
		}
	},
	setup(props, { emit }) {
		const { toastNotification } = useNotifications()

		const mode = ref("FORWARD")
		const selected = ref([])
		const cycleOptions = ref([])
		const loading = ref(false)

		// Próxima generación automática (regla del día 05, igual que el backend):
		// día 05 de este mes si aún no llega, o del próximo mes si ya pasó.
		const EVALUATION_DAY = 5
		const now = moment()
		const forwardStartDate = (
			now.date() < EVALUATION_DAY
				? now.clone().date(EVALUATION_DAY)
				: now.clone().add(1, "month").startOf("month").date(EVALUATION_DAY)
		).format("DD/MM/YYYY")

		const fetchCycles = async () => {
			if (cycleOptions.value.length || loading.value) return

			loading.value = true
			try {
				const { data } = await axios.get("/performances/available-cycles")
				cycleOptions.value = data.cycles.map((cycle) => ({
					label: cycle.label,
					value: cycle.value
				}))
			} catch (error) {
				toastNotification("No se pudieron cargar los meses disponibles.", false)
			} finally {
				loading.value = false
			}
		}

		const emitValue = () => {
			emit(
				"input",
				selected.value.map((cycle) => cycle.value)
			)
		}

		// Al cambiar de modo: en BACKFILL carga los meses; en FORWARD limpia la selección.
		watch(mode, (value) => {
			if (value === "BACKFILL") {
				fetchCycles()
			} else {
				selected.value = []
				emit("input", [])
			}
		})

		// Al desactivar la evaluación, limpiar la selección de backfill.
		watch(
			() => props.hasPerformance,
			(val) => {
				if (!val) {
					mode.value = "FORWARD"
					selected.value = []
					emit("input", [])
				}
			}
		)

		return {
			mode,
			selected,
			cycleOptions,
			loading,
			forwardStartDate,
			emitValue
		}
	}
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
